import { useEffect, useState, useMemo } from 'react';
import { CommonGrid, TopContainer } from '../layouts';
import {
  BigButton,
  TopButtonArea,
  ModalButtonArea,
  DesignTemplates,
  ActionFilters,
  GlobalFilter,
  MiniButton,
  Download,
  SharePopup,
  DeactivatePopup,
} from '../common';
import _ from 'lodash';
import { icons } from '../assets';
import { handleDownloadURL, setTitle, totalItemCount } from '../helpers/jsHelper';
import {
  apiDeleteProjects,
  apiGetDownloadProjectData,
  apiGetDownloadProject,
} from '../helpers/api';
import { useScreenSettings } from '../helpers/configureHelper';
import {
  EDIT_ICON,
  SHARE_ICON,
  DELETE_ICON,
  COLLECTION_ICON,
  DOWNLOAD_ICON,
} from '../constants/assetConstants';
import { getCuratorPath, getParamsFromFilterBy } from '../helpers/urlHelper';
import Button from '../components/Button/Button';
import FileInput from '../components/FileInput/FileInput';
import { useOpenProjectFromFile } from '../components/admin/curator/CuratorHeader/useOpenProjectFromFile';
import useProjects from '../pages/admin/myprojects/useProjects';
import { useUserContext } from '../contexts/UserContext';
import { useFilterContext } from '../contexts/FilterContext';
import InfiniteScroll from '../components/InfiniteScroll/InfiniteScroll';
import { EditProjectPopup } from '../common/EditProjectPopup/EditProjectPopup';
import { myprojectsActions } from '../redux/slicers/my-projects/myProjectsSlicer';
import { useDispatch, useSelector } from 'react-redux';
import { errorToast, successToast } from '../helpers/toastHelper';
import { CARD_TYPE, COMMON_TAGS_MODEL_TYPES, MODEL_TYPES } from '../constants/constants';
import { ThumbnailContainerV2 } from '../common/ThumbnailContainerV2';
import AddToCollectionPopup from '../common/AddToCollectionPopup';
import { download } from '../common/DownloadFileController/downloadFile';
import { PageSearchBar } from '../common/UniversalSearchForm/UniversalSearchForm';
import PageTitle from '../common/PageTitle';
import { UploadProjectButton } from '../common/UploadButton/UploadProjectButton';
import { RefreshIcon } from '../components/icons/RefreshIcon';
import { MY_PROJECTS_ROUTE } from '../constants/routePathConstants';
import { addUpdateParamsInSequence } from '../redux/slicers/filters/filterSlicer';
import FilterSideModalButton from '../common/FilterSideModalButton';
import TopContainerV2 from '../layouts/TopContainerV2';
import FooterOptions from '../layouts/FooterOptions';
import { GlobalDefaultTeamMemberFilter, GlobalSearchTagsFilter } from '../common/GlobalFilter';
import { getFilterOptionsHelper } from '../helpers/dataHelper';
import { ROOM_TYPE } from "../constants/roomConstants";

const { ProjectIcon, CollectionIcon, DownloadIcon } = icons;
// let title = 'My Projects';

export default function MyProjects({
  cardType = CARD_TYPE.PROJECT,
  productId = '',
  title = 'My Projects',
  useGlobalSearch,
  noDataText = "Your projects live here!",
  cardProps,
  designV2,
  updatePageTitle = true,
  singleSelect,
  popupCardsSelected,
  is360Configurator,
  hideStickyHeader = null,
  skeletonContainerClass,
  redirect= true,
  showMenu = true,
  useGlobalFilter = true,
}) {
  const dispatch = useDispatch();
  const filterCtx = useFilterContext();
  const { currentUser, isGuestUser, isGuestLogin } = useUserContext();
  const currentMemberId = currentUser.id;
  const [editItem, setEditItem] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [filterByForLightroom, setFilterByForLightroom] = useState(
    is360Configurator ? ROOM_TYPE.SILO : ''
  );

  const { sortOptions, filterOptions, defaultSortOption, showFavoriteIcon, showTeamMemberFilter } =
    useScreenSettings(cardType);

  // This will disable all action and remove action button on cards (currently used in configurator lighting)
  let disableProjectCardActions;
  if (!showMenu && !redirect) {
    disableProjectCardActions = true;
  }

  const updatedFilterOptions = getFilterOptionsHelper(
    filterOptions,
    disableProjectCardActions,
    is360Configurator
  );

  useEffect(() => {
    if (updatePageTitle) {
      setTitle(title);
    }
    filterCtx.setBreadcrumbs(null);
  }, []);

  const requestParams = useMemo(
    () => ({
      created_by: '',
      hide_lidar_project: true,
      ...(productId && { product: productId }),
      ...(disableProjectCardActions && { project_type: filterByForLightroom }),
    }),
    [currentMemberId, productId, is360Configurator, filterByForLightroom]
  );

  const {
    list,
    loading,
    fetchMoreData,
    hasMore,
    total,
    hasData,

    anySelected,
    allSelected,
    selectedList,
    sortBy,
    filterBy,
    selected,
    onSortChange,
    onFilterByChange,
    refetchList,
    toggleAll,
    toggleItem,
    toggleSingleItem,
    resetSelection,
    addToCollection,
    search,
    onSearchChange,
  } = useProjects({
    requestParams,
    filterByKey: 'project_type',
    defaultSort: defaultSortOption,
    useGlobalSearch,
    filterByOptions: updatedFilterOptions,
    useGlobalFilter,
  });

  useEffect(() => {
    if (disableProjectCardActions) {
      popupCardsSelected({ anySelected, allSelected, selected, selectedList });
    }
  }, [disableProjectCardActions, selected]);

  useEffect(() => {
    if (is360Configurator) {
      handleFilterByChange({ label: 'Silo', value: '3' });
    }
  }, [is360Configurator]);

  useEffect(() => {
    // when user delete single item then I call the refetch API again.
    if (window?.$refetch) {
      refetchList();
      window.$refetch = false;
    }
  }, [list]);

  const onEdit = async (id) => {
    const itemToEdit = list.find((p) => p.id === id);
    setEditItem(itemToEdit);
  };

  async function handleDownloadClick(id) {
    setIsDownloading(true);
    const formData = new FormData();
    if (id) {
      formData.append('id', id);
    } else {
      selected.forEach((id) => {
        formData.append('id', id);
      });
    }
    const res = await apiGetDownloadProject(formData);
    if (res.status) {
      res?.data?.results?.forEach((item) => {
        download({ fileUrl: item.zip_url, fileName: `${item.name}.zip` });
      });
    } else {
      errorToast(res?.message);
    }
    setIsDownloading(false);
  }

  const handleProjectUpdate = (newData) => {
    dispatch(myprojectsActions.update(newData?.data));
    setEditItem(null);
  };

  const handleUploadProjectsFinished = () => {
    // const samePage = window.location.pathname === MY_PROJECTS_ROUTE;
    // successToast(
    //   <div className="d-flex align-items-center gap-2">
    //     Projects upload finished
    //     {samePage && (
    //       <Button
    //         iconAfter={<RefreshIcon width={18} />}
    //         style={{ marginLeft: 'auto', display: 'flex' }}
    //         color="warning" size="xs"
    //         onClick={() => refetchList()}
    //       >
    //         Refresh
    //       </Button>
    //     )}
    // </div>, { autoClose: 7000 })
  };

  const handleFilterByChange = (value) => {
    if (!disableProjectCardActions) {
      dispatch(
        addUpdateParamsInSequence({ filterBy: getParamsFromFilterBy(value, 'project_type') })
      );
    } else {
      setFilterByForLightroom(value.value);
    }
    onFilterByChange(value);
  };

  const NoDataFound = () => {
    return (
      <div className="w-100 m-auto d-flex flex-column justify-content-center align-items-center">
        <h2>{noDataText}</h2>
        <p>All the room setup that you start on imagine will be</p>
        <p>recorded here.</p>
      </div>
    );
  };

  return (
    <CommonGrid
      sticky={hideStickyHeader ? false : true}
      noDataText={search ? `You don't have any ${cardType} with "${search}" name.` : NoDataFound()}
      className={(anySelected || allSelected) && designV2 ? 'show-action-footer' : ''}
      {...(designV2
        ? {
            topContainer: (
              <TopContainerV2
                subClassName={`myFilesFilterContainer ${
                  disableProjectCardActions ? 'extraSmallCards' : ''
                }`}
                globalFilter={
                  !useGlobalSearch && (
                    <div>
                      <GlobalFilter designV2 />
                      <GlobalSearchTagsFilter designV2={designV2} />
                      <GlobalDefaultTeamMemberFilter designV2 />
                    </div>
                  )
                }>
                <>
                  {!useGlobalSearch && (
                    <>
                      {!disableProjectCardActions && (
                        <div className="totalCount">{totalItemCount(total)}</div>
                      )}
                      {/* {!isGuestUser() && !disableProjectCardActions && !isGuestLogin ? (
                        <UploadProjectButton
                          onUploadFinished={handleUploadProjectsFinished}
                          className="black-btn order-lg-5"
                        />
                      ) : null} */}
                      <PageSearchBar
                        designV2
                        model={COMMON_TAGS_MODEL_TYPES.PROJECTS}
                        onSearchChange={onSearchChange}
                        search={search}
                      />
                      {!disableProjectCardActions && (
                        <FilterSideModalButton showTeamMemberFilter={showTeamMemberFilter} />
                      )}
                      <ActionFilters
                        hasData={hasData}
                        loading={loading}
                        filterProps={
                          // (loading || list?.length || filterBy) &&
                          {
                            value: filterBy,
                            data: updatedFilterOptions,
                            onChange: handleFilterByChange,
                            containerClass: 'top-filter',
                          }
                        }
                        sortProps={
                          // (loading || list?.length > 0) &&
                          {
                            disabled: Boolean(search),
                            value: sortBy,
                            data: sortOptions,
                            onChange: onSortChange,
                            containerClass: 'top-filter',
                          }
                        }
                        showTeamMemberFilter={false}
                      />
                    </>
                  )}
                  <div style={{ display: 'flex' }}>
                    {!useGlobalSearch && (
                      <TopButtonArea
                        hasData={hasData}
                        item={cardType}
                        addNewButtonArea={
                          !useGlobalSearch && (
                            <>
                              <ModalButtonArea
                                // bigButton={true}
                                // bigBtnClass="lf-btn-1"
                                // variant="warning"
                                // buttonTitle="New Project"
                                className="new-project"
                                // buttonIcon={ProjectIcon}
                                title={
                                  <>
                                    <h2 className="pg-title">
                                      What type of project do you want to design?
                                    </h2>
                                    <h5>Select one of the ones you see down below.</h5>
                                  </>
                                }
                                size="lg"
                                body={<NewProjectModalBody />}
                              />
                            </>
                          )
                        }
                        anySelected={anySelected}
                        selectedList={selectedList}
                      />
                    )}
                  </div>
                </>
              </TopContainerV2>
            ),
          }
        : {
            topContainer: (
              <TopContainer
                title={
                  !useGlobalSearch && <PageTitle title={title} total={total} search={search} />
                }
                item={cardType}
                hasData={hasData}
                buttonArea={
                  <div style={{ display: 'flex' }}>
                    {!useGlobalSearch && (
                      <TopButtonArea
                        hasData={hasData}
                        item={cardType}
                        topButtonList={[
                          {
                            element: DeactivatePopup,
                            bigButton: true,
                            bigBtnClass: 'lf-btn-3',
                            isPlural: true,
                            iconSrc: DELETE_ICON,
                            deleteAction: apiDeleteProjects,
                            item: cardType,
                            onSuccess: refetchList,
                            actionWord: 'delete',
                          },
                          {
                            element: BigButton,
                            bigBtnClass: 'lf-btn-2',
                            iconBefore: <CollectionIcon />,
                            buttonTitle: 'Add To Collection',
                            onClick: addToCollection,
                          },
                          {
                            element: SharePopup,
                            bigButton: true,
                            bigBtnClass: 'lf-btn-2',
                            onSuccess: refetchList,
                            selectedList,
                            modelType: 4,
                          },
                          // {
                          //   element: BigButton,
                          //   bigBtnClass: 'lf-btn-1',
                          //   isLoading: isDownloading,
                          //   buttonTitle: 'Download',
                          //   iconBefore: <DownloadIcon />,
                          //   onClick: handleDownloadClick,
                          // },
                        ]}
                        addNewButtonArea={
                          !isGuestUser() &&
                          !useGlobalSearch && (
                            <>
                              <ModalButtonArea
                                // bigButton={true}
                                // bigBtnClass="lf-btn-1"
                                // variant="warning"
                                // buttonTitle="New Project"
                                className="new-project"
                                // buttonIcon={ProjectIcon}
                                title={
                                  <>
                                    <h2 className="pg-title">
                                      What type of project do you want to design?
                                    </h2>
                                    <h5>Select one of the ones you see down below.</h5>
                                  </>
                                }
                                size="lg"
                                body={<NewProjectModalBody />}
                              />
                              {/* <UploadProjectButton
                                onUploadFinished={handleUploadProjectsFinished}
                              /> */}
                            </>
                          )
                        }
                        anySelected={anySelected}
                        selectedList={selectedList}
                      />
                    )}
                    {!useGlobalSearch && (
                      <FilterSideModalButton showTeamMemberFilter={showTeamMemberFilter} />
                    )}
                  </div>
                }
                searchBar={
                  !useGlobalSearch && (
                    <div className="mt-2">
                      <PageSearchBar
                        model={COMMON_TAGS_MODEL_TYPES.PROJECTS}
                        onSearchChange={onSearchChange}
                        search={search}
                      />
                    </div>
                  )
                }
              />
            ),
          })}
      item={cardType}
      hasData={list.length > 0 || loading}
      list={list}
      {...(!designV2 &&
        !useGlobalSearch && {
          filterArea: (
            <>
              <ActionFilters
                hasData={hasData}
                loading={loading}
                filterProps={
                  // (loading || list?.length || filterBy) &&
                  {
                    value: filterBy,
                    data: updatedFilterOptions,
                    onChange: handleFilterByChange,
                  }
                }
                sortProps={
                  // (loading || list?.length > 0) &&
                  {
                    value: sortBy,
                    data: sortOptions,
                    onChange: onSortChange,
                  }
                }
                selectedProps={
                  list?.length > 0 && {
                    allSelected: allSelected,
                    onSelectAll: () => toggleAll(),
                  }
                }
                showTeamMemberFilter={false}
              />
              <GlobalFilter />
            </>
          ),
        })}>
      <InfiniteScroll
        dataLength={list.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loading={loading}
        hasChildren={hasData}
        showMenu={showMenu}
        redirect={redirect}
        scrollableTarget="infiniteScroll"
        skeletonContainerClass={skeletonContainerClass}>
        <ThumbnailContainerV2
          items={list}
          modelType={MODEL_TYPES.PROJECTS}
          selected={selected}
          redirect={redirect}
          showMenu={showMenu}
          cardProps={{
            fullWidth: true,
            onEdit: onEdit,
            onSelect: singleSelect ? toggleSingleItem : toggleItem,
            ...cardProps,
          }}
          designV2={designV2}
        />
        {hasMore && !disableProjectCardActions && <div id="whiteShadow" />}
      </InfiniteScroll>

      {(anySelected || allSelected) && designV2 && !disableProjectCardActions && (
        <FooterOptions>
          <>
            <div className="selected-item">
              <ActionFilters
                selectedProps={
                  list?.length > 0 && {
                    allSelected: allSelected,
                    onSelectAll: () => toggleAll(),
                  }
                }
              />
              <span>{selectedList?.length || 0} Selected</span>
            </div>
            <div className="footer-action">
              <TopButtonArea
                hasData={hasData}
                item={cardType}
                topButtonList={[
                  {
                    element: DeactivatePopup,
                    bigButton: true,
                    bigBtnClass: 'lf-btn-3',
                    isPlural: true,
                    iconSrc: DELETE_ICON,
                    deleteAction: apiDeleteProjects,
                    item: cardType,
                    onSuccess: refetchList,
                    actionWord: 'delete',
                  },
                  {
                    element: BigButton,
                    bigBtnClass: 'lf-btn-2',
                    iconBefore: <CollectionIcon />,
                    buttonTitle: 'Add To Collection',
                    onClick: addToCollection,
                  },
                  {
                    element: SharePopup,
                    bigButton: true,
                    bigBtnClass: 'lf-btn-2',
                    onSuccess: refetchList,
                    selectedList,
                    modelType: 4,
                  },
                  // {
                  //   element: BigButton,
                  //   bigBtnClass: 'lf-btn-1',
                  //   isLoading: isDownloading,
                  //   buttonTitle: 'Download',
                  //   iconBefore: <DownloadIcon />,
                  //   onClick: handleDownloadClick,
                  // },
                ]}
                anySelected={anySelected}
                selectedList={selectedList}
              />
            </div>
          </>
        </FooterOptions>
      )}

      <EditProjectPopup
        data={editItem}
        show={Boolean(editItem)}
        onClose={() => setEditItem(null)}
        onUpdateSuccess={handleProjectUpdate}
      />
      <AddToCollectionPopup onSuccess={resetSelection} />
    </CommonGrid>
  );
}

const NewProjectModalBody = () => {
  const { openProjectFromFile } = useOpenProjectFromFile();

  return (
    <>
      {/* <div className="d-flex spaceEvenly fullWidth pb-2 mb-0">
        <FileInput
          Component={Button}
          componentProps={{
            as: 'label',
          }}
          size="xlg"
          color="warning"
          className="FileInputLabel"
          onFileSelect={(file) => openProjectFromFile(file)}
          accept=".zip">
          Open Project
        </FileInput>
      </div> */}
      <DesignTemplates center excludeVideo />
    </>
  );
};
